/**
 * Background Style Fields - Reusable templates for background image/video styling
 *
 * Based on ImageBlock and VideoBlock schemas from @segmently/schema-models.
 * Used for background section style panels.
 *
 * Sprint: background-section
 * Phase: 4 - Config
 */

import type { FieldConfig } from '../../types/editorFieldTypes';
import {
  SCALE_MODE_OPTIONS,
  BOOLEAN_OPTIONS,
  IMAGE_KIND_OPTIONS,
  NUMBER_0_50_OPTIONS,
  NUMBER_0_500_OPTIONS,
} from './options';

/**
 * Image style fields (ImageBlock from schema)
 *
 * Note: scaleMode is intentionally omitted for background images.
 * Backgrounds should always use 'scaleAspectFill' to cover the full screen.
 * The default value from schema will be used.
 */
export const IMAGE_STYLE_FIELDS: FieldConfig[] = [];

/**
 * Video style fields (VideoBlock from schema)
 *
 * Properties: repeat
 * Note: scaleMode is intentionally omitted for background videos.
 * Backgrounds should always use 'scaleAspectFill' to cover the full screen.
 */
export const VIDEO_STYLE_FIELDS: FieldConfig[] = [
  {
    path: '.styles.repeat',
    label: 'repeat',
    type: 'select',
    options: [...BOOLEAN_OPTIONS],
    defaultValue: 'true',
    isNullable: true,
  },
];

/**
 * Create image style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'styles.background.styles.image')
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createImageStyleFields('styles.background.styles.image')
 * // Returns fields with paths like 'styles.background.styles.image.styles.scaleMode'
 */
export function createImageStyleFields(basePath: string): FieldConfig[] {
  return IMAGE_STYLE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Create video style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'styles.background.styles.video')
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createVideoStyleFields('styles.background.styles.video')
 * // Returns fields with paths like 'styles.background.styles.video.styles.repeat'
 */
export function createVideoStyleFields(basePath: string): FieldConfig[] {
  return VIDEO_STYLE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Extended Image style fields (ImageBlock from schema)
 *
 * Properties: imageKind, width, height, scaleMode, mainCornerRadius
 * Box padding is rendered via PaddingElement (cross-layout), not as individual fields.
 * Used for body section images (NOT for background).
 */
export const EXTENDED_IMAGE_STYLE_FIELDS: FieldConfig[] = [
  {
    path: '.styles.imageKind',
    label: 'image-kind',
    type: 'select',
    options: [...IMAGE_KIND_OPTIONS],
    defaultValue: 'ImageKind1',
    isNullable: true,
  },
  {
    path: '.styles.width',
    label: 'image-width',
    type: 'numberCombo',
    options: NUMBER_0_500_OPTIONS,
    defaultScrollValue: 200,
    isNullable: true,
  },
  {
    path: '.styles.height',
    label: 'image-height',
    type: 'numberCombo',
    options: NUMBER_0_500_OPTIONS,
    defaultScrollValue: 300,
    isNullable: true,
  },
  {
    path: '.styles.scaleMode',
    label: 'image-scale-mode',
    type: 'select',
    options: [...SCALE_MODE_OPTIONS],
    defaultValue: 'scaleAspectFit',
    isNullable: true,
  },
  {
    path: '.styles.mainCornerRadius',
    label: 'corner-radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultScrollValue: 20,
    isNullable: true,
  },
  // Box padding is rendered via PaddingElement with paddingConfig
];

/**
 * Create extended image style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'image')
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createExtendedImageStyleFields('image')
 * // Returns fields with paths like 'image.styles.imageKind', 'image.box.styles.paddingLeft'
 */
export function createExtendedImageStyleFields(basePath: string): FieldConfig[] {
  return EXTENDED_IMAGE_STYLE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Simplified Image style fields for Slider and List sections
 *
 * WITHOUT imageKind, width, height - these are not needed for item images in collections.
 * Properties: scaleMode, mainCornerRadius
 *
 * Used for slider items and list items (NOT for body section images).
 */
export const SIMPLE_IMAGE_STYLE_FIELDS: FieldConfig[] = [
  {
    path: '.styles.scaleMode',
    label: 'image-scale-mode',
    type: 'select',
    options: [...SCALE_MODE_OPTIONS],
    defaultValue: 'scaleAspectFit',
    isNullable: true,
  },
  {
    path: '.styles.mainCornerRadius',
    label: 'corner-radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultScrollValue: 20,
    isNullable: true,
  },
];

/**
 * Create simple image style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'list.items.0.image')
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createSimpleImageStyleFields('list.items.0.image')
 * // Returns fields with paths like 'list.items.0.image.styles.imageKind'
 */
export function createSimpleImageStyleFields(basePath: string): FieldConfig[] {
  return SIMPLE_IMAGE_STYLE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * List Image style fields with size controls
 *
 * Properties: width, height, scaleMode, mainCornerRadius
 * Used for list items where image size customization is needed (Table, TwoColumn).
 * Unlike SIMPLE_IMAGE_STYLE_FIELDS, this includes width/height for precise sizing.
 */
export const LIST_IMAGE_STYLE_FIELDS: FieldConfig[] = [
  {
    path: '.styles.width',
    label: 'image-width',
    type: 'numberCombo',
    options: NUMBER_0_500_OPTIONS,
    defaultScrollValue: 100,
    isNullable: true,
  },
  {
    path: '.styles.height',
    label: 'image-height',
    type: 'numberCombo',
    options: NUMBER_0_500_OPTIONS,
    defaultScrollValue: 100,
    isNullable: true,
  },
  {
    path: '.styles.scaleMode',
    label: 'image-scale-mode',
    type: 'select',
    options: [...SCALE_MODE_OPTIONS],
    defaultValue: 'scaleAspectFit',
    isNullable: true,
  },
  {
    path: '.styles.mainCornerRadius',
    label: 'corner-radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultScrollValue: 20,
    isNullable: true,
  },
];

/**
 * Create list image style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'list.items.0.image')
 * @returns Array of FieldConfig with full paths including width/height
 *
 * @example
 * createListImageStyleFields('list.items.0.image')
 * // Returns fields with paths like 'list.items.0.image.styles.width'
 */
export function createListImageStyleFields(basePath: string): FieldConfig[] {
  return LIST_IMAGE_STYLE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}
